import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Avatar,
  Tag,
  Button,
  Spin,
  message,
  Row,
  Col,
  Divider,
  Statistic,
} from "antd";
import {
  ArrowLeftOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  CalendarOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  FacebookOutlined,
  InstagramOutlined,
  BankOutlined,
  FileTextOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";

const ProfileWrapper = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  .back-button {
    margin-bottom: 16px;
    color: #1890ff;
    font-weight: 500;
  }

  .cover-banner {
    height: 200px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px 12px 0 0;
    position: relative;
  }

  .cover-banner img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px 12px 0 0;
  }

  .profile-header {
    background: #fff;
    padding: 24px;
    border-radius: 0 0 12px 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    position: relative;
    margin-bottom: 24px;
  }

  .logo-container {
    position: absolute;
    top: -60px;
    left: 24px;
    background: #fff;
    padding: 8px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .company-info {
    margin-left: 140px;
    padding-top: 8px;
  }

  .company-name {
    font-size: 28px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 8px 0;
  }

  .company-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #595959;
    font-size: 14px;
  }

  .social-links {
    display: flex;
    gap: 12px;
    margin-top: 16px;
  }

  .social-link {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f0f0;
    color: #595959;
    cursor: pointer;
    transition: all 0.3s;
  }

  .social-link:hover {
    background: #1890ff;
    color: #fff;
  }

  .metrics-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-bottom: 24px;
  }

  .metrics-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 20px;
  }

  .summary-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-bottom: 24px;
  }

  .summary-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
  }

  .summary-content {
    color: #595959;
    line-height: 1.8;
    font-size: 14px;
  }

  .details-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .details-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 20px;
  }

  .detail-item {
    margin-bottom: 16px;
  }

  .detail-label {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  .detail-value {
    font-size: 14px;
    color: #262626;
    font-weight: 500;
  }
`;

const OrganizationProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const backPath = location.state?.from || "/admin/job-requirments";
  const backLabel = location.state?.fromLabel || "Back";

  useEffect(() => {
    fetchOrganization();
  }, [id]);

  const fetchOrganization = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const headers = { Authorization: `Bearer ${token}` };

      let response;
      if (id.startsWith("ORGID-")) {
        response = await axios.get(`${API_CONST.GET_ORGANIZATION}/${id}`, { headers });
      } else {
        response = await axios.post(API_CONST.GET_ORGANIZATION_BY_NAME, { name: id }, { headers });
      }

      if (response.data?.organization) {
        setOrganization(response.data.organization);
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
      message.error("Failed to load organization details");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLogo = async (logoPath) => {
    if (!logoPath) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: logoPath },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        return response.data.downloadUrl;
      }
    } catch (error) {
      console.error("Error getting logo URL:", error);
    }
    return null;
  };

  if (loading) {
    return (
      <ProfileWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <p style={{ marginTop: 16 }}>Loading organization details...</p>
        </div>
      </ProfileWrapper>
    );
  }

  if (!organization) {
    return (
      <ProfileWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Organization not found</h2>
          <Button type="primary" onClick={() => navigate(backPath)}>
            {backLabel}
          </Button>
        </div>
      </ProfileWrapper>
    );
  }

  const metrics = organization.metrics || {};
  const isCustomer = organization.type === "customer";

  return (
    <ProfileWrapper>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(backPath)}
        className="back-button"
      >
        {backLabel}
      </Button>

      <div className="cover-banner">
        {organization.coverBanner && (
          <img src={organization.coverBanner} alt="Cover Banner" />
        )}
      </div>

      <div className="profile-header">
        <div className="logo-container">
          <Avatar
            size={100}
            src={organization.logo}
            icon={<BankOutlined />}
            style={{ backgroundColor: "#1890ff" }}
          />
        </div>

        <div className="company-info">
          <h1 className="company-name">{organization.name}</h1>
          <div className="company-meta">
            <Tag color={isCustomer ? "blue" : "green"}>
              {isCustomer ? "Customer" : "Partner"}
            </Tag>
            {organization.industry && (
              <span className="meta-item">
                <BankOutlined /> {organization.industry}
              </span>
            )}
            {organization.website && (
              <a
                href={organization.website.startsWith("http") ? organization.website : `https://${organization.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="meta-item"
                style={{ color: "#1890ff" }}
              >
                <GlobalOutlined /> {organization.website}
              </a>
            )}
            {organization.city && organization.country && (
              <span className="meta-item">
                <EnvironmentOutlined /> {organization.city}, {organization.country}
              </span>
            )}
            {organization.employeeCount && (
              <span className="meta-item">
                <TeamOutlined /> {organization.employeeCount} employees
              </span>
            )}
            {organization.foundedYear && (
              <span className="meta-item">
                <CalendarOutlined /> Founded {organization.foundedYear}
              </span>
            )}
          </div>

          {organization.socialLinks && (
            <div className="social-links">
              {organization.socialLinks.linkedin && (
                <a
                  href={organization.socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <LinkedinOutlined />
                </a>
              )}
              {organization.socialLinks.twitter && (
                <a
                  href={organization.socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <TwitterOutlined />
                </a>
              )}
              {organization.socialLinks.facebook && (
                <a
                  href={organization.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <FacebookOutlined />
                </a>
              )}
              {organization.socialLinks.instagram && (
                <a
                  href={organization.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <InstagramOutlined />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="metrics-card">
        <h3 className="metrics-title">Company Metrics</h3>
        <Row gutter={[24, 24]}>
          {isCustomer ? (
            <>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Total Jobs Created"
                  value={metrics.totalJobsCreated || 0}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Jobs Active"
                  value={metrics.jobsActive || 0}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Jobs Fulfilled"
                  value={metrics.jobsFulfilled || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Total Hired"
                  value={metrics.totalHired || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: "#fa8c16" }}
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Months Active"
                  value={metrics.monthsActive || 0}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: "#13c2c2" }}
                />
              </Col>
            </>
          ) : (
            <>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="Total Talents Deployed"
                  value={metrics.totalTalentsDeployed || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="Active Talents"
                  value={metrics.activeTalents || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="Total Talents Hired"
                  value={metrics.totalTalentsHired || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="Months Active"
                  value={metrics.monthsActive || 0}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: "#13c2c2" }}
                />
              </Col>
            </>
          )}
        </Row>
      </div>

      {organization.summary && (
        <div className="summary-card">
          <h3 className="summary-title">About Company</h3>
          <p className="summary-content">{organization.summary}</p>
        </div>
      )}

      <Row gutter={24}>
        <Col xs={24} md={12}>
          <div className="details-card">
            <h3 className="details-title">Company Information</h3>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div className="detail-item">
                  <div className="detail-label">GST/Registration ID</div>
                  <div className="detail-value">{organization.gstNumber || "-"}</div>
                </div>
              </Col>
              <Col span={12}>
                <div className="detail-item">
                  <div className="detail-label">Employee Count</div>
                  <div className="detail-value">{organization.employeeCount || "-"}</div>
                </div>
              </Col>
              <Col span={12}>
                <div className="detail-item">
                  <div className="detail-label">Industry</div>
                  <div className="detail-value">{organization.industry || "-"}</div>
                </div>
              </Col>
              <Col span={12}>
                <div className="detail-item">
                  <div className="detail-label">Founded Year</div>
                  <div className="detail-value">{organization.foundedYear || "-"}</div>
                </div>
              </Col>
              <Col span={24}>
                <div className="detail-item">
                  <div className="detail-label">Website</div>
                  <div className="detail-value">
                    {organization.website ? (
                      <a href={organization.website.startsWith("http") ? organization.website : `https://${organization.website}`} target="_blank" rel="noopener noreferrer">
                        {organization.website}
                      </a>
                    ) : "-"}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>

        <Col xs={24} md={12}>
          <div className="details-card">
            <h3 className="details-title">Contact Information</h3>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div className="detail-item">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{organization.email || "-"}</div>
                </div>
              </Col>
              <Col span={24}>
                <div className="detail-item">
                  <div className="detail-label">Phone</div>
                  <div className="detail-value">{organization.phone || "-"}</div>
                </div>
              </Col>
              <Col span={24}>
                <div className="detail-item">
                  <div className="detail-label">Address</div>
                  <div className="detail-value">
                    {organization.address || "-"}
                    {organization.city && `, ${organization.city}`}
                    {organization.state && `, ${organization.state}`}
                    {organization.pincode && ` - ${organization.pincode}`}
                    {organization.country && `, ${organization.country}`}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </ProfileWrapper>
  );
};

export default OrganizationProfile;
